import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Download, Copy, Check } from 'lucide-react'
import GlassCard from '@/components/ui/GlassCard'

interface QRModalProps {
  isOpen: boolean
  onClose: () => void
  qrCodeUrl: string
  propertyTitle: string
  propertyUrl: string
}

const QRModal: React.FC<QRModalProps> = ({
  isOpen,
  onClose,
  qrCodeUrl,
  propertyTitle,
  propertyUrl
}) => {
  const [copied, setCopied] = useState(false)

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = qrCodeUrl
    link.download = `qr-${propertyTitle.toLowerCase().replace(/\s+/g, '-')}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(propertyUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Error copying URL:', error)
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md"
        >
          <GlassCard className="p-6">
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">
                  Código QR
                </h2>
                <p className="text-white/70 text-sm line-clamp-1">
                  {propertyTitle}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>

            {/* QR Preview */}
            <div className="bg-white rounded-2xl p-6 mb-6 flex items-center justify-center">
              <img
                src={qrCodeUrl}
                alt="QR Code"
                className="w-64 h-64 object-contain"
              />
            </div>

            {/* Info Text */}
            <div className="text-center mb-6">
              <p className="text-white/90 text-sm mb-3">
                📱 Escanea este código para ver la propiedad
              </p>
              <div className="bg-white/10 rounded-lg px-4 py-3 mb-4">
                <p className="text-white/60 text-xs mb-1">URL de la propiedad:</p>
                <p className="text-white text-sm font-mono truncate">
                  {propertyUrl}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleDownload}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold flex items-center justify-center gap-2 transition-all transform hover:scale-105"
              >
                <Download className="h-5 w-5" />
                Descargar QR HD (1000x1000px)
              </button>

              <button
                onClick={handleCopyUrl}
                className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="h-5 w-5 text-green-400" />
                    ¡Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="h-5 w-5" />
                    Copiar URL
                  </>
                )}
              </button>
            </div>

            {/* Note */}
            <p className="text-white/50 text-xs text-center mt-4">
              El código QR está en alta resolución (1000x1000px) ideal para impresión en lonas y material publicitario
            </p>
          </GlassCard>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default QRModal
